import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, tokenStore, setOnAuthFailure } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  }, []);

  const applySession = useCallback(async (session) => {
    tokenStore.set(session);
    if (session.user) {
      setUser(session.user);
      return session.user;
    }
    return refreshUser();
  }, [refreshUser]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    return applySession(data);
  }, [applySession]);

  const signup = useCallback(async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    return applySession(data);
  }, [applySession]);

  const loginWithGoogle = useCallback(async (session_id) => {
    const { data } = await api.post("/auth/google", { session_id });
    return applySession(data);
  }, [applySession]);

  const logout = useCallback(async () => {
    const refresh_token = tokenStore.refresh;
    try {
      if (refresh_token) await api.post("/auth/logout", { refresh_token });
    } catch (_) {
      /* best-effort */
    }
    tokenStore.clear();
    setUser(null);
  }, []);

  const logoutAll = useCallback(async () => {
    try {
      await api.post("/auth/logout-all");
    } catch (_) {
      /* ignore */
    }
    tokenStore.clear();
    setUser(null);
  }, []);

  // Force logout when a token refresh ultimately fails.
  useEffect(() => {
    setOnAuthFailure(() => {
      setUser(null);
    });
  }, []);

  // Bootstrap: an in-flight Google callback (session_id in the hash) is handled
  // by AuthCallback, so skip the /auth/me probe to avoid a flash + race.
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash.includes("session_id=")) {
      setLoading(false);
      return;
    }
    if (!tokenStore.access) {
      setLoading(false);
      return;
    }
    refreshUser()
      .catch(() => {
        tokenStore.clear();
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, [refreshUser]);

  const value = useMemo(
    () => ({
      user,
      setUser,
      loading,
      isAuthed: !!user,
      login,
      signup,
      loginWithGoogle,
      logout,
      logoutAll,
      refreshUser,
    }),
    [user, loading, login, signup, loginWithGoogle, logout, logoutAll, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
